import React, { useState, useMemo } from 'react';
import { useAsync } from 'react-async-hook';
import { Label, Select, TextInput } from 'flowbite-react';
import * as sched from 'sched';
import { useAuth } from '../../hooks/useAuth';
import { useWorkspace } from '@/context/WorkspaceContext';

interface SelectWorkspaceUserIdProps {
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  multiple?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  className?: string;
}

const SelectWorkspaceUserId: React.FC<SelectWorkspaceUserIdProps> = ({
  value,
  onChange,
  multiple = false,
  label = 'Select User',
  placeholder = 'Search users...',
  disabled = false,
  error,
  className = '',
}) => {
  const { user, token, client } = useAuth();
  const { workspaceId } = useWorkspace();
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch workspace user profiles
  const { result: userProfiles, loading, error: fetchError } = useAsync(async (): Promise<sched.UserProfile[]> => {
    if (!user || !token || !client || !workspaceId) return [];


    const profiles = await sched.WrappedApiClient.list_workspace_user_profiles(
      client,
      token,
      workspaceId
    );

    return profiles.user_profiles || [];
  }, [user, token, client, workspaceId]);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!userProfiles) return [];

    if (!searchTerm.trim()) return userProfiles;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return userProfiles.filter((profile: sched.UserProfile) =>
      profile.display_name.toLowerCase().includes(lowerSearchTerm) ||
      profile.email.toLowerCase().includes(lowerSearchTerm) ||
      profile.id.toLowerCase().includes(lowerSearchTerm)
    );
  }, [userProfiles, searchTerm]);

  // Handle selection change
  const handleSelectionChange = (selectedValue: string) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      if (currentValues.includes(selectedValue)) {
        // Remove if already selected
        onChange(currentValues.filter(v => v !== selectedValue));
      } else {
        // Add to selection
        onChange([...currentValues, selectedValue]);
      }
    } else {
      onChange(selectedValue);
    }
  };

  // Handle removing a selected user (for multiple selection)
  const handleRemoveUser = (userId: string) => {
    if (multiple && Array.isArray(value)) {
      onChange(value.filter(v => v !== userId));
    }
  };

  // Get selected user profiles for display
  const selectedUserProfiles = useMemo(() => {
    if (!userProfiles) return [];

    const selectedIds = multiple
      ? (Array.isArray(value) ? value : [])
      : (value ? [value] : []);

    return userProfiles.filter((profile: sched.UserProfile) => selectedIds.includes(profile.id));
  }, [userProfiles, value, multiple]);

  if (loading) {
    return (
      <div className={className}>
        {label && <Label>{label}</Label>}
        <div className="flex items-center space-x-2 p-2 border border-cm-border-subtle rounded-md bg-cm-bg-elevated">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
          <span className="text-sm text-cm-text-muted">Loading users...</span>
        </div>
      </div>
    );
  }

  if (fetchError) {
    const error = fetchError as unknown as string;
    return (
      <div className={className}>
        {label && <Label>{label}</Label>}
        <div className="p-2 border border-red-300 rounded-md bg-red-50 dark:bg-red-900/20 dark:border-red-600">
          <span className="text-sm text-red-600">
            Failed to load users: {error}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && <Label>{label}</Label>}

      {/* Search input */}
      <div className="mb-2">
        <TextInput
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={disabled}
          className="w-full"
        />
      </div>

      {/* Selected users display (for multiple selection) */}
      {multiple && selectedUserProfiles.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedUserProfiles.map((profile: sched.UserProfile) => (
            <div
              key={profile.id}
              className="flex items-center space-x-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-md text-sm"
            >
              <span>{profile.display_name}</span>
              <button
                type="button"
                onClick={() => handleRemoveUser(profile.id)}
                className="ml-1 text-blue-600 hover:text-blue-800"
                disabled={disabled}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* User selection dropdown/list */}
      <div className="border border-cm-border-subtle rounded-md max-h-48 overflow-y-auto">
        {filteredUsers.length === 0 ? (
          <div className="p-3 text-sm text-cm-text-muted text-center">
            {searchTerm ? 'No users found matching your search' : 'No users available'}
          </div>
        ) : (
          filteredUsers.map((profile: sched.UserProfile) => {
            const isSelected = multiple
              ? (Array.isArray(value) ? value.includes(profile.id) : false)
              : value === profile.id;

            return (
              <div
                key={profile.id}
                className={`p-3 cursor-pointer hover:bg-cm-bg-elevated border-b border-cm-border-subtle last:border-b-0 ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !disabled && handleSelectionChange(profile.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-cm-text-primary">
                      {profile.display_name}
                    </div>
                    <div className="text-sm text-cm-text-muted">
                      {profile.email}
                    </div>
                  </div>
                  {isSelected && (
                    <div className="text-blue-600">
                      ✓
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default SelectWorkspaceUserId;
